import React, { LegacyRef, useEffect, useState } from 'react';
import { Button, Checkbox, Select, Fade, FormControlLabel, FormGroup, Grid, MenuItem, FormControl, InputLabel, FormHelperText, ClickAwayListener } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxSharpIcon from '@mui/icons-material/CheckBoxSharp';
import CheckBoxOutlineBlankSharpIcon from '@mui/icons-material/CheckBoxOutlineBlankSharp';

import './EditTableColumn.scss';
import { useColumnPresets } from '../../providers/ColumnPresets';
import { inputValidation } from '../../helpers/commonHelpers';

interface Props {
  presetDropdownOptions,
  xGridColumns,
  colVisibility,
  setColVisibility,
  setLoading?
}
export default function EditTableColumn({
  presetDropdownOptions = [],
  xGridColumns,
  colVisibility,
  setColVisibility,
  setLoading
}: Props) {
  const { t } = useTranslation();
  const [editColumnsOpen, setEditColumnsOpen] = useState(false);

  const handleEditColumnOpen = () => {setEditColumnsOpen(!editColumnsOpen)};

  return (
    <span>
      <Button className='btn-grey' onClick={handleEditColumnOpen}>
      <span className='icon-edit'></span><span className='edit-text'>{t('editTableColumns.editColumns')}</span>
      </Button>
      <div className='edit-column-container'>
        <Fade in={editColumnsOpen} unmountOnExit>
            <ColumnOptions
              presetDropdownOptions={presetDropdownOptions}
              xGridColumns={xGridColumns}
              colVisibility={colVisibility}
              setColVisibility={setColVisibility}
              setLoading={setLoading}
              setEditColumnsOpen={setEditColumnsOpen}
              handleEditColumnOpen={handleEditColumnOpen}
              editColumnsOpen={editColumnsOpen}
            />
        </Fade>
      </div>
    </span>)
}

interface ColumnsProps {
  presetDropdownOptions: any
  xGridColumns, colVisibility, setColVisibility, setLoading, setEditColumnsOpen, handleEditColumnOpen, editColumnsOpen
}

const ColumnOptions = React.forwardRef(function ({ presetDropdownOptions = [], xGridColumns, colVisibility, setColVisibility, setLoading, setEditColumnsOpen, handleEditColumnOpen, editColumnsOpen }: ColumnsProps, ref) {

  const [presetDropdown, setPresetDropdown] = useState(presetDropdownOptions? presetDropdownOptions[0]: '');
  const [presetName, setPresetName] = useState('default');
  const [loadPresets, setLoadPresets] = useState(false);
  const [presetsSaved, setPresetsSaved] = useState(false);
  const [columnsState, setColumnsState] = useState(colVisibility);
  const [error, setError] = useState(false);
  const [errorMax, setErrorMax] = useState(false);
  const columnPresetsContext = useColumnPresets();
  const { t } = useTranslation();

  const [savePresetNotEntered, setSavePresetNotEntered] = useState(false);

  useEffect(() => {
    const columns = {};
    columnPresetsContext.columnPreferences.columns
      .forEach(col => columns[col.internalCode] = col.preferredDisplay);
    setColumnsState({ ...columns });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnPresetsContext.columnPreferences]);

  useEffect(()=>{
    // flags to set when the Edit Column Open/Close state changes
    if(!editColumnsOpen){      
      setPresetsSaved(false);
      setLoadPresets(false);
      setSavePresetNotEntered(false);
      setError(false);
      setErrorMax(false);
    }     
  },[editColumnsOpen])

  const handleChange = (event) => setColumnsState({ ...columnsState, [event.target.name]: event.target.checked });
  const handleSelectAll = (control) => {
    let tempObject = {};
    for (const key in columnsState) {
      if (columnsState.hasOwnProperty(key) && key !== 'pairingId') {
        tempObject[key] = control;
      }
    }
    setColumnsState({ ...columnsState, ...tempObject });
  };

  const applyColumnsUpdate = () => {
    if (loadPresets) {
      // show toast message "Preset Loaded ?"
      columnPresetsContext.getColumnPreferences(presetDropdown);
      setEditColumnsOpen(false);
      setLoadPresets(false);
    }
    else {
      if (presetsSaved === false) {
        columnPresetsContext.savePresets(columnsState, 'default');
      }
      else {
        columnPresetsContext.savePresets(columnsState, presetName);
      }
      setEditColumnsOpen(false);
      setPresetsSaved(false);
    }
    setLoading(true);
    setColVisibility({ ...columnsState });
  };

  const handleShow = (save = false) => {
    if (!save) {
      setPresetsSaved(false);
      setLoadPresets(!loadPresets);
      return;
    }
    setLoadPresets(false);
    setPresetsSaved(!presetsSaved);
    setError(false);
    setErrorMax(false);
    setPresetName('default');
    if (!presetsSaved) { setSavePresetNotEntered(true); }
    else { setSavePresetNotEntered(false); }
  };

  const handlePresetChange = (e) => {
    if (!e.target.value) {
      return;
    }
    setPresetDropdown(e.target.value);
  };

  const handleValidation = (e) => {
    setSavePresetNotEntered(false);
    if (!inputValidation(e.target.value.trim()) ) {
      setError(true);
      setErrorMax(false);
    }else if (e.target.value.length >= e.target.maxLength) {
      setErrorMax(true);
      setError(false);
    }else {
      setError(false);
      setErrorMax(false);
      setPresetName(e.target.value.trim());
    }
  }
  
  return (<ClickAwayListener onClickAway={handleEditColumnOpen} mouseEvent='onMouseUp'>


<div className='edit-column-modal' ref={ref as LegacyRef<HTMLDivElement>}>
  <Grid container item justifyContent='space-between' spacing={2}>
    <Grid item>
      <div className='modal-title'> {t('editTableColumns.title')}</div>
    </Grid>
    <Grid item>
      <CloseIcon className='icon-close-modal' onClick={handleEditColumnOpen} />
    </Grid>
  </Grid>
  <Grid container>
    <Grid item xs={12}>
      <div className='edit-columns-list'>
        <FormGroup>
          {xGridColumns.map((option, index) =>
            (option.field !== 'pairingId' && option.field !== 'id' && option.field !== 'actions') && (
              <FormControlLabel
                key={index}
                control={<Checkbox className='styledCheckBox' checked={columnsState[option.field]} 
                icon={<CheckBoxOutlineBlankSharpIcon className='check-box-icon-outline' />}
                checkedIcon={<CheckBoxSharpIcon className='check-box-con-solid' />}
                onChange={handleChange} name={option.field} disableRipple />}
                label={option.headerName}
              />
            )
          )}
        </FormGroup>
      </div>
    </Grid>
  </Grid>
  <hr className='MuiDivider-root'></hr>
  <Grid container direction='column' spacing={2}>
    <Grid container item justifyContent='flex-end' alignItems='flex-end'>
      <Button className='modal-link' disableRipple disableElevation onClick={() => handleSelectAll(false)}>
        <span className='link-states'>{t('editTableColumns.clearAll')}</span>
      </Button>
      <Button className='modal-link' disableRipple disableElevation onClick={() => handleSelectAll(true)}>
        <span className='link-states'>{t('editTableColumns.selectAll')}</span>
      </Button>
    </Grid>

    <Grid container item  alignItems='center'>
      <Button className='btn-modal-grey btn-loadpreset' disabled={presetsSaved || !presetDropdownOptions || presetDropdownOptions.length === 0} onClick={() => handleShow()}> {t('editTableColumns.loadPreset')} </Button>
    <Button className='btn-modal-grey btn-savepreset' disabled={loadPresets} onClick={() => handleShow(true)}> {t('editTableColumns.savePreset')} </Button>
    </Grid>
    <Grid container item xs={12}>
      {loadPresets ?
        (
          <div className="preset-options">
            <div className='form-label'>{t('editTableColumns.loadPresetLabel')} : </div>
            <FormControl fullWidth variant='outlined' className='column-preset-dropdown'>
              <InputLabel htmlFor="column-preset-dropdown">{t('editTableColumns.savePresetLabel')}</InputLabel>
              <Select labelId="column-preset-dropdown" label={t('editTableColumns.savePresetLabel')} value={presetDropdown} onChange={(e) => handlePresetChange(e)} >
                {presetDropdownOptions.map((option, i) => {
                  return (
                    <MenuItem key={i} value={option}>{option}</MenuItem>
                  );
                })
                }
              </Select>
            </FormControl>

          </div>
        )
        : presetsSaved ? (
          <div className='preset-options'>
            <div className='form-label'>{t('editTableColumns.savePresetTitle')} : </div>
            <FormGroup className="column-preset-input">
              <input type="text"
                className= {error || errorMax  ? 'edit-table-preset-name-input text-error' : 'edit-table-preset-name-input'}
                onKeyUp={(e) => handleValidation(e)} minLength = {0} maxLength = {31}             
                />
              <FormHelperText id="preset-name-error">
                { 
                  error && <><i className='icon-circle-flight' /><span>{t('editTableColumns.error-preset-name')}</span></>
                }
                {errorMax && <><i className='icon-circle-flight' /><span>{t('editTableColumns.preset-max-error')}</span></>}
              </FormHelperText>
            </FormGroup>
          </div>
        ) : ''}
    </Grid>
    <Grid container item xs={12}>
    <Button className='btn-modal-black' disabled={error || savePresetNotEntered || errorMax } onClick={() => applyColumnsUpdate()}> {!presetsSaved && !loadPresets ? t('editTableColumns.apply') : presetsSaved ? t('editTableColumns.saveApply') : t('editTableColumns.loadApply')}</Button>
    </Grid>
  </Grid>
</div>
</ClickAwayListener>)
});